import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { LoadingButton } from "./Spinner";
import { useToast } from "./Toast";
import { useConfirm } from "./ConfirmDialog";
import RelativeTime from "./RelativeTime";

interface Props {
  workspaceId: string;
  flowId: string;
  flowName: string;
  open: boolean;
  onClose: () => void;
}

type Destination =
  | { id?: string; kind: "slack"; config: { webhook_url: string } }
  | { id?: string; kind: "email"; config: { to: string } };

// Light client-side cron check so typos surface inline instead of as a
// server error toast. The server's parseCron stays authoritative.
function cronLooksValid(spec: string): boolean {
  const parts = spec.trim().split(/\s+/);
  if (parts.length !== 5) return false;
  const field = /^(\*|\d+(-\d+)?)(\/\d+)?(,(\*|\d+(-\d+)?)(\/\d+)?)*$/;
  return parts.every((p) => field.test(p));
}

function destinationValid(d: Destination): boolean {
  if (d.kind === "slack") {
    try {
      const u = new URL(d.config.webhook_url);
      return u.protocol === "https:";
    } catch { return false; }
  }
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.config.to);
}

// Schedule management for a single flow. List existing, add a new one,
// edit / delete. Alerts table at the bottom of each row.
export default function ScheduleModal({ workspaceId, flowId, flowName, open, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const list = useQuery({
    queryKey: ["schedules", workspaceId],
    queryFn: () => api.schedules.list(workspaceId),
    enabled: open,
  });
  const envsQ = useQuery({
    queryKey: ["environments", workspaceId],
    queryFn: () => api.environments.list(workspaceId),
    enabled: open,
  });
  const flowSchedules = useMemo(
    () => (list.data?.schedules ?? []).filter((s) => s.flow_id === flowId),
    [list.data, flowId],
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Create-new form state.
  const [newName, setNewName] = useState("Hourly check");
  const [newCron, setNewCron] = useState("0 * * * *");
  const [newEnvId, setNewEnvId] = useState<string>("");
  const [newAlertFailure, setNewAlertFailure] = useState(true);
  const [newAlertSuccess, setNewAlertSuccess] = useState(false);
  const [newDestinations, setNewDestinations] = useState<Destination[]>([]);

  const create = useMutation({
    mutationFn: () => api.schedules.create({
      workspace_id: workspaceId,
      flow_id: flowId,
      name: newName.trim() || "Scheduled run",
      cron: newCron.trim(),
      environment_id: newEnvId || null,
      enabled: true,
      alert_on_failure: newAlertFailure,
      alert_on_success: newAlertSuccess,
      destinations: newDestinations.map((d) => ({ kind: d.kind, config: d.config })),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["schedules", workspaceId] });
      toast.success("Schedule created");
      setNewName("Hourly check"); setNewCron("0 * * * *"); setNewEnvId("");
      setNewAlertFailure(true); setNewAlertSuccess(false); setNewDestinations([]);
    },
    onError: (e) => toast.error(String((e as Error).message)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.schedules.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["schedules", workspaceId] }),
  });

  const toggle = useMutation({
    mutationFn: (s: { id: string; enabled: boolean }) => api.schedules.update(s.id, { enabled: s.enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["schedules", workspaceId] }),
    onError: (e) => toast.error(String((e as Error).message)),
  });

  const testAlert = useMutation({
    mutationFn: (id: string) => api.schedules.testAlert(id),
    onSuccess: (out) => {
      const failed = out.results.filter((r) => r.status !== "delivered");
      if (failed.length === 0) toast.success("Test alert delivered");
      else toast.error(`Test alert: ${failed.length} destination(s) failed — ${failed[0].detail ?? "unknown error"}`);
    },
    onError: (e) => toast.error(String((e as Error).message)),
  });

  const cronOk = cronLooksValid(newCron);
  const destsOk = newDestinations.every(destinationValid);
  const canCreate = cronOk && destsOk && !create.isPending;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] grid place-items-center p-4 bg-[color:var(--backdrop)]" onClick={onClose}>
      <div className="card w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-[var(--shadow-strong)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <h2 className="text-base font-semibold">Schedules — {flowName}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="h-8 w-8 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2">×</button>
        </div>
        <div className="flex-1 min-h-0 overflow-auto p-5 space-y-5">
          <div className="card p-4 space-y-3">
            <h3 className="font-semibold">Existing schedules</h3>
            {flowSchedules.length === 0 && (
              <p className="text-sm muted">No schedules for this flow yet.</p>
            )}
            <ul className="space-y-2">
              {flowSchedules.map((s) => (
                <li key={s.id} className="rounded border border-border p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className={"h-2.5 w-2.5 rounded-full shrink-0 " + (s.enabled ? "bg-good" : "bg-text-faint")} title={s.enabled ? "enabled" : "disabled"} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{s.name}</div>
                      <div className="text-xs muted-strong">
                        <code className="mono">{s.cron}</code>
                        {s.last_dispatched_at && (
                          <> · last fired <RelativeTime iso={s.last_dispatched_at} /></>
                        )}
                        {s.last_status && (
                          <> · last <span className={s.last_status === "failed" || s.last_status === "errored" ? "text-danger" : "text-good"}>{s.last_status}</span></>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn text-xs"
                      disabled={toggle.isPending}
                      title={s.enabled ? "Pause this schedule" : "Resume this schedule"}
                      onClick={() => toggle.mutate({ id: s.id, enabled: !s.enabled })}
                    >{s.enabled ? "Pause" : "Resume"}</button>
                    {s.destinations.length > 0 && (
                      <button
                        type="button"
                        className="btn text-xs"
                        disabled={testAlert.isPending}
                        title="Send a test alert to every destination now"
                        onClick={() => testAlert.mutate(s.id)}
                      >{testAlert.isPending ? "Sending…" : "Test alert"}</button>
                    )}
                    <button
                      type="button"
                      className="btn text-xs"
                      onClick={async () => {
                        if (await confirm({ title: `Delete "${s.name}"?`, danger: true })) remove.mutate(s.id);
                      }}
                    >Delete</button>
                  </div>
                  {s.destinations.length > 0 && (
                    <div className="text-xs muted">Alerts: {s.destinations.map((d) => d.kind).join(", ")}</div>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="card p-4 space-y-3">
            <h3 className="font-semibold">New schedule</h3>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr] gap-3">
              <Field label="Name"><input className="input w-full" value={newName} onChange={(e) => setNewName(e.target.value)} /></Field>
              <Field label="Cron (UTC)">
                <input
                  className={"input mono w-full " + (cronOk ? "" : "!border-danger")}
                  value={newCron}
                  onChange={(e) => setNewCron(e.target.value)}
                  placeholder="*/5 * * * *"
                  aria-invalid={!cronOk}
                />
                {!cronOk && <p className="text-xs text-danger mt-1">Needs 5 space-separated fields, e.g. <code className="mono">0 * * * *</code></p>}
              </Field>
            </div>
            <Field label="Environment">
              <select className="input w-full sm:w-72" value={newEnvId} onChange={(e) => setNewEnvId(e.target.value)}>
                <option value="">— No environment —</option>
                {(envsQ.data?.environments ?? []).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </Field>
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={newAlertFailure} onChange={(e) => setNewAlertFailure(e.target.checked)} />
                Alert on failure
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={newAlertSuccess} onChange={(e) => setNewAlertSuccess(e.target.checked)} />
                Alert on success
              </label>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label !m-0">Alert destinations</label>
                <div className="flex gap-2">
                  <button type="button" className="btn text-xs" title="Add a Slack incoming-webhook alert" onClick={() => setNewDestinations([...newDestinations, { kind: "slack", config: { webhook_url: "" } }])}>+ Slack</button>
                  <button type="button" className="btn text-xs" title="Add an email alert" onClick={() => setNewDestinations([...newDestinations, { kind: "email", config: { to: "" } }])}>+ Email</button>
                </div>
              </div>
              <ul className="space-y-2">
                {newDestinations.map((d, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="chip bg-surface-3 muted-strong text-[10px]">{d.kind}</span>
                    <input
                      className={"input flex-1 min-w-0 mono text-xs " + (destinationValid(d) || (d.kind === "slack" ? !d.config.webhook_url : !d.config.to) ? "" : "!border-danger")}
                      placeholder={d.kind === "slack" ? "https://hooks.slack.com/services/…" : "alerts@example.com"}
                      value={d.kind === "slack" ? d.config.webhook_url : d.config.to}
                      onChange={(e) => {
                        const next = newDestinations.slice();
                        if (d.kind === "slack") next[i] = { kind: "slack", config: { webhook_url: e.target.value } };
                        else next[i] = { kind: "email", config: { to: e.target.value } };
                        setNewDestinations(next);
                      }}
                    />
                    <button type="button" className="btn text-xs" aria-label="Remove destination" title="Remove destination" onClick={() => setNewDestinations(newDestinations.filter((_, j) => j !== i))}>×</button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-xs muted">
              Cron uses standard 5-field POSIX syntax in UTC. Examples: <code className="mono">*/5 * * * *</code> (every 5 minutes), <code className="mono">0 9 * * 1-5</code> (09:00 on weekdays), <code className="mono">0 0 1 * *</code> (midnight first of the month).
            </div>
            <div className="flex justify-end">
              <LoadingButton className="btn-primary" pending={create.isPending} disabled={!canCreate} onClick={() => create.mutate()}>Create schedule</LoadingButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="label">{label}</label>
      {children}
    </div>
  );
}
